<template>
  <div class="pending-fee-items-table">
    <!-- Toolbar -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <span v-if="selectedItems.length > 0" class="me-3">
          {{ selectedItems.length }} item(s) selected, total:
          <strong>{{ formatMoney(selectedTotal) }} €</strong>
        </span>
      </div>
      <div class="d-flex gap-2">
        <button
          v-if="selectedItems.length > 0"
          class="btn btn-primary btn-sm"
          @click="openSettlementPreview"
        >
          Settle selected items
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="openCreateModal">
          <font-awesome-icon icon="plus" class="me-1" />
          Add Fee Item
        </button>
      </div>
    </div>

    <DataTable
      v-model:filters="filters"
      v-model:selection="selectedItems"
      :value="enrichedItems"
      dataKey="id"
      filterDisplay="row"
      class="border rounded"
      :scrollable="enrichedItems.length > 20"
      :scrollHeight="enrichedItems.length > 20 ? '600px' : undefined"
      sortField="created_at"
      :sortOrder="-1"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">No pending fee items</div>
      </template>

      <!-- Selection checkbox -->
      <Column selectionMode="multiple" :headerStyle="{ width: '3rem' }" />

      <!-- Description -->
      <Column
        field="description"
        header="Description"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            placeholder="Search..."
          />
        </template>
      </Column>

      <!-- Item Type -->
      <Column
        field="item_type"
        header="Type"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ formatItemType(data.item_type) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueItemTypes"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                Active
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Amount -->
      <Column
        field="amount"
        header="Qty"
        sortable
        :headerStyle="{ width: '4rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
      </Column>

      <!-- Unit Price -->
      <Column
        field="unit_price"
        header="Unit Price"
        sortable
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ data.unit_price.toFixed(4) }} €
        </template>
      </Column>

      <!-- Total excl. VAT -->
      <Column
        field="total_excl_vat"
        header="Total"
        sortable
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ formatMoney(data.total_excl_vat) }} €
        </template>
      </Column>

      <!-- VAT % -->
      <Column
        field="vat_fraction"
        header="VAT %"
        sortable
        :headerStyle="{ width: '5rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ ((data.vat_fraction ?? 0) * 100).toFixed(0) }}%
        </template>
      </Column>

      <!-- Total with VAT -->
      <Column
        field="total_with_vat"
        header="Total (incl. VAT)"
        sortable
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ formatMoney(data.total_with_vat) }} €
        </template>
      </Column>

      <!-- Created Date -->
      <Column
        field="created_at"
        header="Date"
        sortable
        :headerStyle="{ width: '7rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ formatDateTimeIntoDate(data.created_at) }}
        </template>
      </Column>

      <!-- Actions -->
      <Column header="" :headerStyle="{ width: '7rem' }">
        <template #body="{ data }">
          <div class="d-flex gap-1">
            <button
              class="btn btn-sm btn-outline-secondary"
              title="Edit"
              @click="openEditModal(data)"
            >
              <font-awesome-icon icon="pencil-alt" />
            </button>
            <button
              class="btn btn-sm btn-outline-danger"
              title="Cancel"
              @click="openDeleteConfirmation(data)"
            >
              <font-awesome-icon icon="trash" />
            </button>
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Create/Edit Modal -->
    <FeeItemFormModal
      :is-visible="showFormModal"
      :company-id="companyId"
      :company-country="companyCountry"
      :edit-item="editingItem"
      @saved="onItemSaved"
      @close="showFormModal = false"
    />

    <!-- Delete Confirmation -->
    <ConfirmationModalComponent
      :visible="showDeleteConfirmation"
      title="Cancel Fee Item"
      :message="`Are you sure you want to cancel this fee item?\n\n${deletingItem?.description ?? ''}`"
      confirm-button-text="Cancel Item"
      confirm-button-class="btn-danger"
      :preserve-whitespace="true"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirmation = false"
    />

    <!-- Settlement Preview -->
    <InvoiceSettlementPreview
      :show="showSettlementPreview"
      :company-id="companyId"
      :items="selectedItems"
      :company-defaults="companyDefaults"
      :recipient-candidates="recipientCandidates"
      @close="showSettlementPreview = false"
      @settled="onSettled"
    />
  </div>
</template>

<script setup lang="ts">
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { sumBy } from "lodash-es";
import { computed, ref, watch } from "vue";

import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import { formatDateTimeIntoDate, formatMoney } from "@/composables/formatUtils";
import { fromUnits } from "@/composables/money/moneyUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { InvoiceItem } from "@/types";

import FeeItemFormModal from "./FeeItemFormModal.vue";
import InvoiceSettlementPreview from "./InvoiceSettlementPreview.vue";
import type { CompanyDefaults, PreviewItem, RecipientCandidate } from "./types";
import { formatItemType, toPreviewItem } from "./utils";
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

type EnrichedItem = InvoiceItem & PreviewItem;

const { globalErrorHandler } = useGlobalHandlers();

const props = defineProps<{
  items: InvoiceItem[];
  companyId: number;
  companyCountry?: string;
  companyDefaults: CompanyDefaults | null;
  recipientCandidates: RecipientCandidate[];
}>();
const emit = defineEmits<{
  settled: [];
  refresh: [];
}>();

const selectedItems = ref<EnrichedItem[]>([]);

// Form modal state
const showFormModal = ref(false);
const editingItem = ref<InvoiceItem | null>(null);

// Delete confirmation state
const showDeleteConfirmation = ref(false);
const deletingItem = ref<InvoiceItem | null>(null);

// Settlement preview state
const showSettlementPreview = ref(false);

const filters = ref({
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  item_type: { value: null as string[] | null, matchMode: FilterMatchMode.IN },
});

const enrichedItems = computed<EnrichedItem[]>(() =>
  props.items.map(toPreviewItem)
);

const uniqueItemTypes = computed(() => {
  const types = new Set<string>();
  for (const item of enrichedItems.value) {
    types.add(item.item_type);
  }
  return [...types].sort().map((v) => ({ label: formatItemType(v), value: v }));
});

// Sum the exact ledger units, then convert once: summing euro floats can land a sub-cent total
// just under a half cent and print a cent short of what the settled fee document charges.
const selectedTotal = computed(() =>
  fromUnits(sumBy(selectedItems.value, "gross_units"))
);

// Clear selection when items change (after settlement)
watch(
  () => props.items,
  () => {
    selectedItems.value = [];
  }
);

function openSettlementPreview() {
  if (selectedItems.value.length === 0) return;
  showSettlementPreview.value = true;
}

function onSettled() {
  showSettlementPreview.value = false;
  emit("settled");
}

function openCreateModal() {
  editingItem.value = null;
  showFormModal.value = true;
}

function openEditModal(item: InvoiceItem) {
  editingItem.value = item;
  showFormModal.value = true;
}

function onItemSaved() {
  showFormModal.value = false;
  emit("refresh");
}

function openDeleteConfirmation(item: InvoiceItem) {
  deletingItem.value = item;
  showDeleteConfirmation.value = true;
}

async function confirmDelete() {
  if (!deletingItem.value) return;

  try {
    await goSolidApi.delete(
      `/api/admin/company/${props.companyId}/invoicing/fee-items/${deletingItem.value.id}`
    );
    showDeleteConfirmation.value = false;
    deletingItem.value = null;
    emit("refresh");
  } catch (error: unknown) {
    globalErrorHandler(error);
  }
}
</script>
